/**
 * 3D coordinate grid + axes gnomon lenses (DESIGNS-TRACK-Y3).
 */

import { projectWorldPoint } from "../../render/camera";
import type { LensPlugin } from "../types";

function strokeSegment(
  g: Parameters<LensPlugin["draw"]>[1],
  dim: number,
  x0: number,
  y0: number,
  z0: number,
  x1: number,
  y1: number,
  z1: number,
  color: string,
  width: number,
  alpha: number,
): void {
  const a = projectWorldPoint(x0, y0, z0, g.render, g.width, g.height, dim);
  const b = projectWorldPoint(x1, y1, z1, g.render, g.width, g.height, dim);
  if (!a.visible && !b.visible && dim >= 3) {
    // still draw if either is on-screen-ish
  }
  const { ctx } = g;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  ctx.lineTo(b.x, b.y);
  ctx.stroke();
  ctx.restore();
}

export const grid3dLens: LensPlugin = {
  id: "grid3d",
  label: "3D grid",
  enabled: (r) => r.showGrid === true,
  draw(view, g) {
    const dim = view.dim;
    const ext = Math.max(0.1, g.render.gridDrawExtent ?? 1);
    const spacing = Math.max(0.05, g.render.gridSpacing ?? 0.25);
    const alpha = Math.min(1, Math.max(0.05, g.render.gridOpacity ?? 0.35));
    const steps = Math.min(40, Math.max(1, Math.round((2 * ext) / spacing)));
    const color = "rgba(148, 163, 184, 1)";

    // Lines parallel to axes on the three mid-planes (or XY only in 2D)
    const planes: ("xy" | "xz" | "yz")[] =
      dim >= 3 ? ["xy", "xz", "yz"] : ["xy"];

    for (const plane of planes) {
      for (let s = 0; s <= steps; s++) {
        const t = -ext + (2 * ext * s) / steps;
        if (plane === "xy") {
          // z=0: lines // x and // y
          strokeSegment(g, dim, -ext, t, 0, ext, t, 0, color, 1, alpha);
          strokeSegment(g, dim, t, -ext, 0, t, ext, 0, color, 1, alpha);
        } else if (plane === "xz") {
          strokeSegment(g, dim, -ext, 0, t, ext, 0, t, color, 1, alpha * 0.85);
          strokeSegment(g, dim, t, 0, -ext, t, 0, ext, color, 1, alpha * 0.85);
        } else {
          strokeSegment(g, dim, 0, -ext, t, 0, ext, t, color, 1, alpha * 0.85);
          strokeSegment(g, dim, 0, t, -ext, 0, t, ext, color, 1, alpha * 0.85);
        }
      }
    }
  },
};

export const gnomonLens: LensPlugin = {
  id: "gnomon",
  label: "Axes gnomon",
  enabled: (r) => r.showGnomon === true,
  draw(view, g) {
    if (view.dim < 3) return;
    const len = Math.max(0.15, (g.render.gridDrawExtent ?? 1) * 0.35);
    const w = 2;
    strokeSegment(g, view.dim, 0, 0, 0, len, 0, 0, "#ef4444", w, 0.95); // +X red
    strokeSegment(g, view.dim, 0, 0, 0, 0, len, 0, "#22c55e", w, 0.95); // +Y green
    strokeSegment(g, view.dim, 0, 0, 0, 0, 0, len, "#3b82f6", w, 0.95); // +Z blue
  },
};
